import { AlertTriangle, CheckCircle2, Info, XCircle, type LucideIcon } from "lucide-react";
import type { CSSProperties, KeyboardEvent, ReactNode } from "react";

import { cx } from "./controls";

export function Card({
  title,
  subtitle,
  actions,
  children,
  flush,
  onClick,
  className,
  style,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  flush?: boolean;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
}) {
  const interactive = !!onClick;
  const onKey = (e: KeyboardEvent) => {
    if (interactive && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick?.();
    }
  };
  return (
    <section
      className={cx("ds-card", flush && "ds-card--flush", interactive && "ds-card--interactive", className)}
      onClick={onClick}
      onKeyDown={onKey}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      style={style}
    >
      {title || actions ? (
        <header className="ds-card__header">
          <div style={{ minWidth: 0 }}>
            {title ? <h3 className="ds-card__title">{title}</h3> : null}
            {subtitle ? <p className="ds-card__subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="ds-actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="ds-card__body">{children}</div>
    </section>
  );
}

export type Tone = "neutral" | "success" | "warning" | "danger" | "info" | "accent";

export function Badge({ tone = "neutral", children, dot, title }: { tone?: Tone; children: ReactNode; dot?: boolean; title?: string }) {
  return (
    <span className={cx("ds-badge", tone !== "neutral" && `ds-badge--${tone}`)} title={title}>
      {dot ? <span className="ds-dot" /> : null}
      {children}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, description, action }: { icon?: LucideIcon; title: string; description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="ds-empty">
      {Icon ? (
        <span className="ds-empty__icon">
          <Icon size={24} />
        </span>
      ) : null}
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

export function Skeleton({ width = "100%", height = 14, style }: { width?: number | string; height?: number | string; style?: CSSProperties }) {
  return <div className="ds-skel" style={{ width, height, ...style }} />;
}

export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} width={`${90 - (i % 3) * 15}%`} />
      ))}
    </div>
  );
}

const BANNER_ICONS: Record<string, LucideIcon> = { info: Info, warning: AlertTriangle, danger: XCircle, success: CheckCircle2 };

export function Banner({ tone = "info", title, children, action }: { tone?: "info" | "warning" | "danger" | "success"; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const Icon = BANNER_ICONS[tone];
  const color = tone === "danger" ? "var(--danger)" : tone === "warning" ? "var(--warning)" : tone === "success" ? "var(--success)" : "var(--info)";
  return (
    <div className={cx("ds-banner", `ds-banner--${tone}`)} role={tone === "danger" ? "alert" : "status"}>
      <Icon size={18} color={color} />
      <div className="ds-grow">
        {title ? <div style={{ fontWeight: 650 }}>{title}</div> : null}
        {children ? <div className="ds-small" style={{ color: "var(--text-muted)" }}>{children}</div> : null}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, eyebrow }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <header className="ds-page-header">
      <div style={{ minWidth: 0 }}>
        {eyebrow ? <div className="ds-small ds-faint" style={{ marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{eyebrow}</div> : null}
        <h1 className="ds-page-title">{title}</h1>
        {subtitle ? <p className="ds-page-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="ds-actions">{actions}</div> : null}
    </header>
  );
}

export function Stat({ icon: Icon, label, value, tone, onClick, loading }: { icon: LucideIcon; label: ReactNode; value: ReactNode; tone?: "success" | "warning" | "danger" | "info"; onClick?: () => void; loading?: boolean }) {
  return (
    <Card onClick={onClick} flush>
      <div className="ds-stat">
        <span className={cx("ds-stat__icon", tone && `ds-stat__icon--${tone}`)}>
          <Icon size={20} />
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="ds-stat__value">{loading ? <Skeleton width={48} height={22} /> : value}</div>
          <div className="ds-stat__label">{label}</div>
        </div>
      </div>
    </Card>
  );
}

export function Avatar({ name }: { name?: string | null }) {
  const initials = (name ?? "?")
    .split(/[\s._@-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  return <span className="ds-avatar" title={name ?? undefined}>{initials || "?"}</span>;
}

export function formatRelative(iso?: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = (Date.now() - t) / 1000;
  if (diff < -60) {
    const ahead = -diff;
    if (ahead < 3600) return `in ${Math.round(ahead / 60)} min`;
    if (ahead < 86400) return `in ${Math.round(ahead / 3600)} h`;
    if (ahead < 86400 * 14) return `in ${Math.round(ahead / 86400)} d`;
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  }
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
